import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DistributionGroup, User } from '@org/interfaces';
import { AuthService, DistributionGroupService, SysNotifyService, UserService } from '@org/shared-services';

/** System roles from GET /api/users/getroles that should not appear in distro group membership */
const DISTRO_GROUP_EXCLUDED_ROLE_NAMES = ['developer', 'tech', 'vendor', 'am', 'user'];

type SystemRoleOption = { _id: string; name: string; description?: string };
import { ConfirmationModalTWComponent } from '../confirmation-dialog/confirmation-modal-tw/confirmation-modal-tw.component';

type GroupFormState = {
  _id?: string;
  name: string;
  description: string;
  isGlobal: boolean;
  userIds: string[];
  roleIds: string[];
};

@Component({
  selector: 'lib-distribution-groups-manager',
  imports: [CommonModule, FormsModule, ConfirmationModalTWComponent],
  templateUrl: './distribution-groups-manager.component.html',
  styleUrl: './distribution-groups-manager.component.scss',
})
export class DistributionGroupsManagerComponent implements OnInit, OnChanges {
  @Input() isOpen = false;
  @Input() tenantId = '';
  @Output() closed = new EventEmitter<void>();
  @Output() groupsChanged = new EventEmitter<void>();

  globalGroups: DistributionGroup[] = [];
  privateGroups: DistributionGroup[] = [];
  tenantUsers: { _id: string; name: string; email?: string }[] = [];
  systemRoles: SystemRoleOption[] = [];

  editing: GroupFormState | null = null;
  showForm = false;
  saving = false;
  deleteTarget: DistributionGroup | null = null;
  showDeleteModal = false;

  canCreateGlobal = false;
  loadingGroups = false;

  constructor(
    private distroGroupService: DistributionGroupService,
    private userService: UserService,
    private authService: AuthService,
    private sysNotify: SysNotifyService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const roles = this.authService.currentUserValue?.roles ?? [];
    this.canCreateGlobal = roles.includes('gm') || roles.includes('admin') || roles.includes('developer');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.loadSystemRoles();
    }
    if ((changes['isOpen'] || changes['tenantId']) && this.isOpen && this.tenantId) {
      this.loadTenantData();
    }
    if (changes['isOpen']) {
      this.cdr.markForCheck();
    }
  }

  close(): void {
    this.showForm = false;
    this.editing = null;
    this.closed.emit();
  }

  loadSystemRoles(): void {
    this.userService.getRoles().subscribe({
      next: (roles) => {
        this.systemRoles = roles
          .filter(
            (r: { _id?: string; name: string }) =>
              r._id && !DISTRO_GROUP_EXCLUDED_ROLE_NAMES.includes(r.name)
          )
          .map((r: { _id: string; name: string; description?: string }) => ({
            _id: r._id,
            name: r.name,
            description: r.description,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.cdr.markForCheck();
      },
      error: () => this.sysNotify.showError('Could not load system roles', 4000, 'right', 'top'),
    });
  }

  loadTenantData(): void {
    if (!this.tenantId) {
      return;
    }
    this.loadingGroups = true;
    this.distroGroupService.getDistributionGroupsByTenant(this.tenantId).subscribe({
      next: (res) => {
        this.globalGroups = res.data.filter((g) => g.isGlobal);
        this.privateGroups = res.data.filter((g) => !g.isGlobal);
        this.loadingGroups = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingGroups = false;
        this.cdr.markForCheck();
        this.sysNotify.showError('Could not load distribution groups', 4000, 'right', 'top');
      },
    });

    this.userService.getUsersByTenant(this.tenantId).subscribe({
      next: (users: User[]) => {
        this.tenantUsers = users
          .filter(
            (u: User) =>
              u._id &&
              u.isActive &&
              !u.roles?.some((r) => r.name === 'developer' || r.name === 'tech')
          )
          .map((u: User) => ({ _id: u._id as string, name: u.name, email: u.email }))
          .sort((a, b) => a.name.localeCompare(b.name));
      },
    });
  }

  startCreate(isGlobal: boolean): void {
    if (isGlobal && !this.canCreateGlobal) {
      return;
    }
    this.editing = {
      name: '',
      description: '',
      isGlobal,
      userIds: [],
      roleIds: [],
    };
    this.showForm = true;
  }

  startEdit(group: DistributionGroup): void {
    const userIds = (group.users ?? []).map((u) => (typeof u === 'string' ? u : u._id));
    const roleIds = (group.roles ?? []).map((r) => (typeof r === 'string' ? r : r._id));
    this.editing = {
      _id: group._id,
      name: group.name,
      description: group.description ?? '',
      isGlobal: group.isGlobal,
      userIds,
      roleIds,
    };
    this.showForm = true;
  }

  toggleUserId(userId: string, checked: boolean): void {
    if (!this.editing) return;
    if (checked) {
      if (!this.editing.userIds.includes(userId)) {
        this.editing.userIds = [...this.editing.userIds, userId];
      }
    } else {
      this.editing.userIds = this.editing.userIds.filter((id) => id !== userId);
    }
  }

  toggleRoleId(roleId: string, checked: boolean): void {
    if (!this.editing) return;
    if (checked) {
      if (!this.editing.roleIds.includes(roleId)) {
        this.editing.roleIds = [...this.editing.roleIds, roleId];
      }
    } else {
      this.editing.roleIds = this.editing.roleIds.filter((id) => id !== roleId);
    }
  }

  cancelForm(): void {
    this.showForm = false;
    this.editing = null;
  }

  save(): void {
    if (!this.editing || !this.tenantId) return;
    const trimmedName = this.editing.name.trim();
    if (!trimmedName) {
      this.sysNotify.showError('Group name is required', 3000, 'right', 'top');
      return;
    }
    if (this.editing.userIds.length === 0 && this.editing.roleIds.length === 0) {
      this.sysNotify.showError('Select at least one role or user', 3000, 'right', 'top');
      return;
    }

    this.saving = true;
    const payload = {
      name: trimmedName,
      description: this.editing.description?.trim() || undefined,
      users: this.editing.userIds,
      roles: this.editing.roleIds,
    };

    const request$ = this.editing._id
      ? this.distroGroupService.updateDistributionGroup(this.editing._id, payload)
      : this.distroGroupService.createDistributionGroup({
          ...payload,
          tenantId: this.tenantId,
          isGlobal: this.editing.isGlobal,
        });

    request$.subscribe({
      next: (saved) => {
        this.saving = false;
        this.showForm = false;
        this.editing = null;
        if (saved?._id) {
          const list = saved.isGlobal ? this.globalGroups : this.privateGroups;
          const idx = list.findIndex((g) => g._id === saved._id);
          if (idx >= 0) {
            list[idx] = saved;
          } else {
            if (saved.isGlobal) {
              this.globalGroups = [...this.globalGroups, saved];
            } else {
              this.privateGroups = [...this.privateGroups, saved];
            }
          }
          this.cdr.markForCheck();
        }
        this.loadTenantData();
        this.groupsChanged.emit();
        this.sysNotify.showSuccess('Distribution group saved', 3000, 'right', 'top');
      },
      error: (err) => {
        this.saving = false;
        const msg = err?.error?.message || err?.message || 'Could not save group';
        this.sysNotify.showError(msg, 5000, 'right', 'top');
      },
    });
  }

  confirmDelete(group: DistributionGroup): void {
    this.deleteTarget = group;
    this.showDeleteModal = true;
  }

  deleteGroup(): void {
    if (!this.deleteTarget) return;
    this.distroGroupService.deleteDistributionGroup(this.deleteTarget._id).subscribe({
      next: () => {
        this.showDeleteModal = false;
        this.deleteTarget = null;
        this.loadTenantData();
        this.groupsChanged.emit();
        this.sysNotify.showSuccess('Distribution group removed', 3000, 'right', 'top');
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || 'Could not delete group';
        this.sysNotify.showError(msg, 5000, 'right', 'top');
      },
    });
  }

  memberSummary(group: DistributionGroup): string {
    const roleCount = group.roles?.length ?? 0;
    const userCount = group.users?.length ?? 0;
    const parts: string[] = [];
    if (roleCount) parts.push(`${roleCount} role${roleCount === 1 ? '' : 's'}`);
    if (userCount) parts.push(`${userCount} user${userCount === 1 ? '' : 's'}`);
    return parts.length ? parts.join(', ') : 'No members';
  }
}
